import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';
import {
  getWedding,
  getWeddingGuests,
  getWeddingGuestStats,
  updateWeddingGuest,
  deleteWeddingGuest,
  addWeddingGuest,
  uploadWeddingGuestReceipt,
  bulkImportWeddingGuests,
  api,
} from '../../../lib/api';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import GuestStatsCards from './GuestStatsCards';
import GuestTable from './GuestTable';
import GuestEditModal from './GuestEditModal';

// ---------------------------------------------------------------------------
// Multilingual WA message templates
// ---------------------------------------------------------------------------
const WA_MESSAGES = {
  en: {
    new: (name, couple, details, url) =>
      `Hi ${name}! 🎉\n\nYou are cordially invited to *${couple}*'s wedding!${details ? `\n\n📅 ${details}` : ''}\n\nPlease confirm your attendance:\n${url}\n\nWe look forward to celebrating with you! ❤️`,
    pending: (name, couple, details, url) =>
      `Hi ${name}! 👋\n\nFriendly reminder to confirm your attendance at *${couple}*'s wedding.${details ? `\n\n📅 ${details}` : ''}\n\nRSVP here:\n${url}\n\nThank you! 🙏`,
    declined: (name, couple) =>
      `Hi ${name}, thank you for letting us know. Your kind wishes for *${couple}* mean a lot. We hope to see you another time! 🌸`,
    confirmed: (name, couple, details, _url, guest) =>
      `Hi ${name}! ✅\n\nThank you for confirming *${couple}*'s wedding!${details ? `\n\n📅 ${details}` : ''}\n\n${guest.number_of_guests || 1} person(s).${guest.checkin_token ? `\n\n🎟️ Your check-in code: ${guest.checkin_token}` : ''}\n\nSee you there! ❤️`,
    boilerplate: '\n---\nCreate your wedding invitation for free: https://ourday.my.id',
  },
  id: {
    new: (name, couple, details, url) =>
      `Halo ${name}! 🎉\n\nAnda diundang ke pernikahan *${couple}*!${details ? `\n\n📅 ${details}` : ''}\n\nSilakan konfirmasi kehadiran:\n${url}\n\nKami menantikan kehadiran Anda! ❤️`,
    pending: (name, couple, details, url) =>
      `Halo ${name}! 👋\n\nPengingat untuk mengonfirmasi kehadiran di pernikahan *${couple}*.${details ? `\n\n📅 ${details}` : ''}\n\nRSVP di sini:\n${url}\n\nTerima kasih! 🙏`,
    declined: (name, couple) =>
      `Halo ${name}, terima kasih sudah memberi tahu. Doa dan harapan Anda untuk *${couple}* sangat berarti. Semoga bertemu di lain kesempatan! 🌸`,
    confirmed: (name, couple, details, _url, guest) =>
      `Halo ${name}! ✅\n\nTerima kasih telah mengonfirmasi pernikahan *${couple}*!${details ? `\n\n📅 ${details}` : ''}\n\n${guest.number_of_guests || 1} orang.${guest.checkin_token ? `\n\n🎟️ Kode check-in: ${guest.checkin_token}` : ''}\n\nSampai jumpa! ❤️`,
    boilerplate: '\n---\nBuat undangan pernikahan gratis: https://ourday.my.id',
  },
  ms: {
    new: (name, couple, details, url) =>
      `Hai ${name}! 🎉\n\nAnda dijemput ke majlis perkahwinan *${couple}*!${details ? `\n\n📅 ${details}` : ''}\n\nSila sahkan kehadiran anda:\n${url}\n\nKami menanti kehadiran anda! ❤️`,
    pending: (name, couple, details, url) =>
      `Hai ${name}! 👋\n\nPeringatan untuk mengesahkan kehadiran di majlis *${couple}*.${details ? `\n\n📅 ${details}` : ''}\n\nRSVP di sini:\n${url}\n\nTerima kasih! 🙏`,
    declined: (name, couple) =>
      `Hai ${name}, terima kasih kerana memaklumkan. Doa anda untuk *${couple}* amat bermakna. Semoga bertemu di lain kesempatan! 🌸`,
    confirmed: (name, couple, details, _url, guest) =>
      `Hai ${name}! ✅\n\nTerima kasih kerana mengesahkan majlis *${couple}*!${details ? `\n\n📅 ${details}` : ''}\n\n${guest.number_of_guests || 1} orang.${guest.checkin_token ? `\n\n🎟️ Kod check-in: ${guest.checkin_token}` : ''}\n\nJumpa di sana! ❤️`,
    boilerplate: '\n---\nCipta jemputan perkahwinan percuma: https://ourday.my.id',
  },
  zh: {
    new: (name, couple, details, url) =>
      `${name} 您好！🎉\n\n诚挚邀请您参加 *${couple}* 的婚礼！${details ? `\n\n📅 ${details}` : ''}\n\n请确认出席：\n${url}\n\n期待与您共庆！❤️`,
    pending: (name, couple, details, url) =>
      `${name} 您好！👋\n\n温馨提醒，请确认参加 *${couple}* 的婚礼。${details ? `\n\n📅 ${details}` : ''}\n\n请在此回复：\n${url}\n\n谢谢！🙏`,
    declined: (name, couple) =>
      `${name} 您好，感谢您的回复。您对 *${couple}* 的祝福对我们意义重大。希望将来有机会相见！🌸`,
    confirmed: (name, couple, details, _url, guest) =>
      `${name} 您好！✅\n\n感谢您确认参加 *${couple}* 的婚礼！${details ? `\n\n📅 ${details}` : ''}\n\n${guest.number_of_guests || 1} 位。${guest.checkin_token ? `\n\n🎟️ 签到码：${guest.checkin_token}` : ''}\n\n届时见！❤️`,
    boilerplate: '\n---\n免费创建婚礼请柬：https://ourday.my.id',
  },
};

// ---------------------------------------------------------------------------
// CSV parser — flexible header detection
// ---------------------------------------------------------------------------
const HEADER_MAP = {
  name: 'guest_name', guest_name: 'guest_name', nama: 'guest_name',
  phone: 'phone', telepon: 'phone', hp: 'phone', no_hp: 'phone', 'no hp': 'phone',
  email: 'email',
  pax: 'number_of_guests', guests: 'number_of_guests', number_of_guests: 'number_of_guests', jumlah: 'number_of_guests',
};

function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/).filter(Boolean);
  if (lines.length < 2) return [];

  const rawHeaders = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));
  const mappedHeaders = rawHeaders.map(h => HEADER_MAP[h] || null);

  const guests = [];
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
    const guest = {};
    mappedHeaders.forEach((key, idx) => {
      if (key && values[idx]) {
        guest[key] = key === 'number_of_guests' ? parseInt(values[idx], 10) || 1 : values[idx];
      }
    });
    if (guest.guest_name) guests.push(guest);
  }
  return guests;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const GuestManagementTab = ({ weddingId }) => {
  const { token } = useAdminAuth();
  const [guests, setGuests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [editingGuest, setEditingGuest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [waMessage, setWaMessage] = useState(null);
  const [receiptView, setReceiptView] = useState(null);
  const [tableInitialized, setTableInitialized] = useState(true);
  const [initializingTable, setInitializingTable] = useState(false);
  const [planLimit, setPlanLimit] = useState(null);
  const [weddingContent, setWeddingContent] = useState(null);
  const [weddingSlug, setWeddingSlug] = useState(null);
  const [isFree, setIsFree] = useState(true);

  // CSV import state
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [csvGuests, setCsvGuests] = useState([]);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvError, setCsvError] = useState(null);
  const fileInputRef = useRef(null);

  // Send-all queue state
  const [sendQueue, setSendQueue] = useState(null); // { guests: [], currentIdx, sent, failed, stopped }
  const sendQueueRef = useRef(null);

  // Fetch wedding content + subscription plan
  useEffect(() => {
    if (!token || !weddingId) return;
    getWedding(token, weddingId).then(data => {
      const content = typeof data.wedding?.content === 'string' ? JSON.parse(data.wedding.content) : data.wedding?.content;
      setWeddingContent(content);
      setWeddingSlug(data.wedding?.slug);
      const planId = data.wedding?.plan_id || 'free';
      setIsFree(planId === 'free');
    }).catch(() => {});
  }, [token, weddingId]);

  const fetchData = useCallback(async () => {
    if (!token) return;
    try {
      let guestData, statsData;

      if (weddingId) {
        [guestData, statsData] = await Promise.all([
          getWeddingGuests(token, weddingId, { search, status: statusFilter }),
          getWeddingGuestStats(token, weddingId),
        ]);
      } else {
        [guestData, statsData] = await Promise.all([
          api.getGuests(token, { search, status: statusFilter }),
          api.getGuestStats(token),
        ]);
      }

      setGuests(guestData.guests || []);
      setStats(statsData);
      setError(null);
      setTableInitialized(true);
      if (guestData.limitedByPlan) {
        setPlanLimit({ totalGuests: guestData.totalGuests, planLimit: guestData.planLimit });
      } else {
        setPlanLimit(null);
      }
    } catch (err) {
      if (err.message?.includes('rsvp_submissions') || err.message?.includes('relation')) {
        setTableInitialized(false);
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [token, weddingId, search, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ---------------------------------------------------------------------------
  // WA message builder
  // ---------------------------------------------------------------------------
  const buildWaMessage = (guest) => {
    const lang = weddingContent?.settings?.language || 'en';
    const templates = WA_MESSAGES[lang] || WA_MESSAGES.en;
    const couple = weddingContent?.couple || {};
    const event = weddingContent?.event || {};
    const schedule = weddingContent?.schedule?.items || [];
    const coupleNames = couple.names || 'our';

    const ceremony = schedule.find(s => s.title?.toLowerCase().includes('ceremony') || s.title?.toLowerCase().includes('akad'));
    const reception = schedule.find(s => s.title?.toLowerCase().includes('reception') || s.title?.toLowerCase().includes('resepsi'));
    const mainEvent = ceremony || reception || schedule[0];
    const eventDate = mainEvent?.date || event.date || event.dateFormatted || '';
    const eventTime = mainEvent?.time || event.time || '';
    const eventPlace = mainEvent?.location || mainEvent?.venue || event.ceremony?.venue || event.venue || '';
    const eventDetails = [eventDate, eventTime, eventPlace].filter(Boolean).join(', ');

    const baseUrl = weddingSlug ? `https://ourday.my.id/w/${weddingSlug}` : '';
    const guestParam = encodeURIComponent(guest.guest_name);
    const pax = guest.number_of_guests || 1;
    const phoneParam = guest.phone ? `&phone=${encodeURIComponent(guest.phone)}` : '';
    const inviteUrl = baseUrl ? `${baseUrl}?guest=${guestParam}&guests=${pax}${phoneParam}` : '';

    const status = guest.status || 'new';
    const templateFn = templates[status] || templates.new;
    let message = templateFn(guest.guest_name, coupleNames, eventDetails, inviteUrl, guest);

    if (isFree) {
      message += templates.boilerplate;
    }

    return message;
  };

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handleInitializeTable = async () => {
    setInitializingTable(true);
    try {
      await api.createRsvpTable(token);
      setTableInitialized(true);
      await fetchData();
    } catch (err) {
      setError('Failed to create table: ' + err.message);
    } finally {
      setInitializingTable(false);
    }
  };

  const handleToggleField = async (id, field, value) => {
    try {
      if (weddingId) {
        await updateWeddingGuest(token, weddingId, id, { [field]: value });
      } else {
        await api.updateGuest(id, { [field]: value }, token);
      }
      setGuests(prev => prev.map(g => g.id === id ? { ...g, [field]: value } : g));
      if (field === 'status') {
        if (weddingId) {
          const statsData = await getWeddingGuestStats(token, weddingId);
          setStats(statsData);
        } else {
          const statsData = await api.getGuestStats(token);
          setStats(statsData);
        }
      }
    } catch (err) {
      console.error('Toggle error:', err);
    }
  };

  const handleSaveGuest = async (formData) => {
    setIsSaving(true);
    try {
      if (editingGuest) {
        if (weddingId) {
          await updateWeddingGuest(token, weddingId, editingGuest.id, formData);
        } else {
          await api.updateGuest(editingGuest.id, formData, token);
        }
      } else {
        if (weddingId) {
          await addWeddingGuest(token, weddingId, {
            guest_name: formData.guest_name,
            email: formData.email,
            phone: formData.phone,
            attending_ceremony: formData.attending_ceremony,
            attending_reception: formData.attending_reception,
            meal_preference: formData.meal_preference,
            dietary_restrictions: formData.dietary_restrictions,
            plus_one_attending: formData.plus_one_attending,
            plus_one_name: formData.plus_one_name,
            plus_one_meal: formData.plus_one_meal,
            special_requests: formData.special_requests,
            number_of_guests: formData.number_of_guests,
          });
        } else {
          await api.submitRsvp({
            guestName: formData.guest_name,
            email: formData.email,
            phone: formData.phone,
            attendingCeremony: formData.attending_ceremony,
            attendingReception: formData.attending_reception,
            mealPreference: formData.meal_preference,
            dietaryRestrictions: formData.dietary_restrictions,
            plusOneAttending: formData.plus_one_attending,
            plusOneName: formData.plus_one_name,
            plusOneMeal: formData.plus_one_meal,
            specialRequests: formData.special_requests,
            numberOfGuests: formData.number_of_guests,
          });
        }
      }
      setShowModal(false);
      setEditingGuest(null);
      await fetchData();
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteGuest = async (id) => {
    try {
      if (weddingId) {
        await deleteWeddingGuest(token, weddingId, id);
      } else {
        await api.deleteGuest(id, token);
      }
      await fetchData();
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const handleEditGuest = (guest) => {
    setEditingGuest(guest);
    setShowModal(true);
  };

  const handleAddGuest = () => {
    setEditingGuest(null);
    setShowModal(true);
  };

  const handleSendWA = async (guest) => {
    setWaMessage({ guest, sending: false, sent: false, error: null });
  };

  const handleUploadReceipt = async (guestId, file) => {
    try {
      let result;
      if (weddingId) {
        result = await uploadWeddingGuestReceipt(token, weddingId, guestId, file);
      } else {
        result = await api.uploadReceipt(guestId, file, token);
      }
      setGuests(prev => prev.map(g => g.id === guestId ? { ...g, gift_receipt_url: result.url, gift_received: true } : g));
      if (editingGuest?.id === guestId) {
        setEditingGuest(prev => ({ ...prev, gift_receipt_url: result.url, gift_received: true }));
      }
    } catch (err) {
      console.error('Receipt upload error:', err);
    }
  };

  const handleViewReceipt = (guest) => {
    if (guest.gift_receipt_url) {
      setReceiptView(guest);
    }
  };

  // ---------------------------------------------------------------------------
  // confirmSendWA — status-aware message
  // ---------------------------------------------------------------------------
  const confirmSendWA = async () => {
    if (!waMessage?.guest) return;
    setWaMessage(prev => ({ ...prev, sending: true }));
    try {
      const guest = waMessage.guest;
      const message = buildWaMessage(guest);
      const sessionName = weddingId ? `wedding-${weddingId}` : 'default';
      await api.sendWhatsappMessage(guest.phone, message, token, sessionName);

      // Auto-transition new → pending
      const updates = { wa_sent: true };
      if (guest.status === 'new') updates.status = 'pending';

      if (weddingId) {
        await updateWeddingGuest(token, weddingId, guest.id, updates);
      } else {
        await api.updateGuest(guest.id, updates, token);
      }
      setWaMessage(prev => ({ ...prev, sending: false, sent: true }));
      setGuests(prev => prev.map(g => g.id === guest.id ? { ...g, ...updates } : g));

      if (updates.status) {
        // Refresh stats
        const statsData = weddingId ? await getWeddingGuestStats(token, weddingId) : await api.getGuestStats(token);
        setStats(statsData);
      }

      setTimeout(() => setWaMessage(null), 2000);
    } catch (err) {
      setWaMessage(prev => ({ ...prev, sending: false, error: err.message }));
    }
  };

  // ---------------------------------------------------------------------------
  // CSV Import
  // ---------------------------------------------------------------------------
  const handleCsvFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvError(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = parseCSV(ev.target.result);
        if (parsed.length === 0) {
          setCsvError('No valid guests found. Ensure CSV has headers: name, phone, email, pax');
          return;
        }
        setCsvGuests(parsed);
      } catch {
        setCsvError('Failed to parse CSV file');
      }
    };
    reader.readAsText(file);
  };

  const handleCsvImport = async () => {
    if (!csvGuests.length || !weddingId) return;
    setCsvImporting(true);
    setCsvError(null);
    try {
      await bulkImportWeddingGuests(token, weddingId, csvGuests);
      setShowCsvModal(false);
      setCsvGuests([]);
      await fetchData();
    } catch (err) {
      setCsvError(err.message);
    } finally {
      setCsvImporting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Send All Invitations (rate-limited queue)
  // ---------------------------------------------------------------------------
  const newGuestsWithPhone = guests.filter(g => g.status === 'new' && g.phone);

  const handleSendAll = () => {
    if (!newGuestsWithPhone.length) return;
    const queue = { guests: [...newGuestsWithPhone], currentIdx: 0, sent: 0, failed: 0, stopped: false };
    setSendQueue(queue);
    sendQueueRef.current = queue;
  };

  const handleStopQueue = () => {
    if (sendQueueRef.current) {
      sendQueueRef.current.stopped = true;
    }
    setSendQueue(prev => prev ? { ...prev, stopped: true } : null);
  };

  // Process queue
  useEffect(() => {
    if (!sendQueue || sendQueue.stopped || sendQueue.currentIdx >= sendQueue.guests.length) return;

    const processNext = async () => {
      const q = sendQueueRef.current;
      if (!q || q.stopped) return;

      const idx = q.currentIdx;
      if (idx >= q.guests.length) {
        setSendQueue(prev => prev ? { ...prev, stopped: true } : null);
        return;
      }

      const guest = q.guests[idx];
      try {
        const message = buildWaMessage(guest);
        const sessionName = weddingId ? `wedding-${weddingId}` : 'default';
        await api.sendWhatsappMessage(guest.phone, message, token, sessionName);

        const updates = { wa_sent: true, status: 'pending' };
        if (weddingId) {
          await updateWeddingGuest(token, weddingId, guest.id, updates);
        } else {
          await api.updateGuest(guest.id, updates, token);
        }
        setGuests(prev => prev.map(g => g.id === guest.id ? { ...g, ...updates } : g));
        q.sent++;
      } catch {
        q.failed++;
      }

      q.currentIdx++;
      setSendQueue({ ...q });

      // If more to send and not stopped, schedule next after 60s
      if (q.currentIdx < q.guests.length && !q.stopped) {
        setTimeout(processNext, 60000);
      } else {
        // Done — refresh stats
        try {
          const statsData = weddingId ? await getWeddingGuestStats(token, weddingId) : await api.getGuestStats(token);
          setStats(statsData);
        } catch { /* ignore */ }
      }
    };

    // Start immediately for first message, otherwise timer handles it
    if (sendQueue.currentIdx === 0 && sendQueue.sent === 0 && sendQueue.failed === 0) {
      processNext();
    }
  }, [sendQueue?.currentIdx === 0 && sendQueue?.sent === 0]); // eslint-disable-line react-hooks/exhaustive-deps

  // Table not created yet - show initialization UI
  if (!tableInitialized && !loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Guest Management</h2>
            <p className="text-muted-foreground mt-1">Manage your invitees, track RSVPs, and send messages.</p>
          </div>
        </div>
        <div className="bg-card rounded-xl border border-border p-8 text-center">
          <Icon name="Database" size={48} className="mx-auto mb-4 text-primary/50" />
          <h3 className="text-lg font-semibold mb-2">Database Setup Required</h3>
          <p className="text-muted-foreground mb-6">
            The guest management table needs to be created before you can start managing guests.
          </p>
          <Button onClick={handleInitializeTable} disabled={initializingTable}>
            {initializingTable ? 'Creating Table...' : 'Initialize Guest Database'}
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-muted-foreground">Loading guests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Guest Management</h2>
          <p className="text-muted-foreground mt-1">Manage your invitees, track RSVPs, and send messages.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {weddingId && (
            <Button variant="outline" onClick={() => { setShowCsvModal(true); setCsvGuests([]); setCsvError(null); }} iconName="Upload" iconPosition="left">
              Import CSV
            </Button>
          )}
          <Button onClick={handleAddGuest} iconName="Plus" iconPosition="left">
            Add New Guest
          </Button>
        </div>
      </div>

      {planLimit && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Icon name="AlertTriangle" size={18} className="text-amber-600" />
            <p className="text-sm text-amber-800">
              <strong>Free plan limit reached</strong> — Showing {planLimit.planLimit} of {planLimit.totalGuests} guests.
              Upgrade to Pro to see all guests.
            </p>
          </div>
          <button
            onClick={() => {
              const base = weddingId ? `/dashboard/weddings/${weddingId}` : '/admin-dashboard';
              window.location.href = `${base}?tab=settings&subtab=billing`;
            }}
            className="px-4 py-2 bg-primary text-white text-sm font-bold rounded-lg hover:bg-primary/90 transition-colors whitespace-nowrap"
          >
            Upgrade Plan
          </button>
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
          <button onClick={fetchData} className="ml-2 underline">Retry</button>
        </div>
      )}

      {/* Send All Invitations Banner */}
      {newGuestsWithPhone.length > 0 && !sendQueue && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Icon name="Send" size={18} className="text-blue-600" />
            <p className="text-sm text-blue-800">
              <strong>{newGuestsWithPhone.length} new guest(s)</strong> with phone numbers ready to receive invitations.
            </p>
          </div>
          <Button onClick={handleSendAll} iconName="Send" iconPosition="left" size="sm">
            Send All Invitations
          </Button>
        </div>
      )}

      {/* Send Queue Progress */}
      {sendQueue && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Icon name="MessageCircle" size={18} className="text-emerald-600" />
              <p className="text-sm text-emerald-800 font-medium">
                Sending invitations... {sendQueue.sent + sendQueue.failed}/{sendQueue.guests.length}
                {sendQueue.failed > 0 && <span className="text-red-600 ml-1">({sendQueue.failed} failed)</span>}
              </p>
            </div>
            {!sendQueue.stopped && sendQueue.currentIdx < sendQueue.guests.length && (
              <Button variant="outline" size="sm" onClick={handleStopQueue}>
                Stop
              </Button>
            )}
          </div>
          <div className="w-full h-2 bg-emerald-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 transition-all duration-300"
              style={{ width: `${((sendQueue.sent + sendQueue.failed) / sendQueue.guests.length) * 100}%` }}
            />
          </div>
          {sendQueue.currentIdx < sendQueue.guests.length && !sendQueue.stopped && (
            <p className="text-xs text-emerald-600">
              Current: {sendQueue.guests[sendQueue.currentIdx]?.guest_name}
              {sendQueue.currentIdx > 0 && ' (next message in ~60s)'}
            </p>
          )}
          {(sendQueue.stopped || sendQueue.currentIdx >= sendQueue.guests.length) && (
            <div className="flex items-center justify-between">
              <p className="text-xs text-emerald-600">
                {sendQueue.stopped && sendQueue.currentIdx < sendQueue.guests.length ? 'Stopped by user.' : 'All done!'}
                {' '}Sent: {sendQueue.sent}, Failed: {sendQueue.failed}
              </p>
              <Button variant="outline" size="sm" onClick={() => setSendQueue(null)}>
                Dismiss
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Stats */}
      <GuestStatsCards stats={stats} />

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search guests..."
            className="w-full pl-10 pr-4 py-2.5 bg-card border border-border rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary/20 transition-all"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2.5 bg-card border border-border rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary/20"
        >
          <option value="">All Status</option>
          <option value="new">New</option>
          <option value="confirmed">Confirmed</option>
          <option value="pending">Pending</option>
          <option value="declined">Declined</option>
        </select>
        <button
          onClick={fetchData}
          className="px-4 py-2.5 bg-card border border-border rounded-lg text-sm hover:bg-muted transition-colors"
          title="Refresh"
        >
          <Icon name="RefreshCw" size={18} />
        </button>
      </div>

      {/* Table */}
      <GuestTable
        guests={guests}
        onToggleField={handleToggleField}
        onEdit={handleEditGuest}
        onDelete={handleDeleteGuest}
        onSendWA={handleSendWA}
        onViewReceipt={handleViewReceipt}
      />

      {/* Edit/Add Modal */}
      {showModal && (
        <GuestEditModal
          guest={editingGuest}
          onSave={handleSaveGuest}
          onClose={() => { setShowModal(false); setEditingGuest(null); }}
          isSaving={isSaving}
          onUploadReceipt={handleUploadReceipt}
        />
      )}

      {/* Receipt Viewer */}
      {receiptView && (
        <div className="fixed inset-0 z-1200 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-card rounded-xl shadow-romantic-lg border border-border max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-foreground">Transfer Receipt - {receiptView.guest_name}</h3>
              <button onClick={() => setReceiptView(null)} className="p-1 rounded-full hover:bg-muted">
                <Icon name="X" size={20} />
              </button>
            </div>
            <img src={receiptView.gift_receipt_url} alt="Receipt" className="w-full rounded-lg" />
          </div>
        </div>
      )}

      {/* WhatsApp Confirm Dialog */}
      {waMessage && (
        <div className="fixed inset-0 z-1200 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-card rounded-xl shadow-romantic-lg border border-border max-w-sm w-full p-6">
            {waMessage.sent ? (
              <div className="text-center">
                <Icon name="CheckCircle" size={48} className="mx-auto mb-3 text-emerald-500" />
                <p className="font-semibold text-foreground">Message Sent!</p>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Icon name="MessageCircle" size={20} className="text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">Send WhatsApp Message</h3>
                    <p className="text-sm text-muted-foreground">to {waMessage.guest.guest_name}</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mb-2">
                  Phone: {waMessage.guest.phone}
                </p>
                <p className="text-xs text-muted-foreground mb-4 italic">
                  Status: {waMessage.guest.status || 'new'} — message template will match this status
                </p>
                {waMessage.error && (
                  <p className="text-sm text-red-600 mb-3">{waMessage.error}</p>
                )}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setWaMessage(null)}
                    className="flex-1"
                    disabled={waMessage.sending}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={confirmSendWA}
                    className="flex-1"
                    disabled={waMessage.sending}
                  >
                    {waMessage.sending ? 'Sending...' : 'Send Message'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* CSV Import Modal */}
      {showCsvModal && (
        <div className="fixed inset-0 z-1200 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-card rounded-xl shadow-romantic-lg border border-border max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-foreground">Import Guests from CSV</h3>
              <button onClick={() => setShowCsvModal(false)} className="p-1 rounded-full hover:bg-muted">
                <Icon name="X" size={20} />
              </button>
            </div>

            <p className="text-sm text-muted-foreground mb-4">
              Upload a CSV file with headers: <code className="bg-muted px-1 rounded">name, phone, email, pax</code>
            </p>

            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.txt"
              onChange={handleCsvFileChange}
              className="w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-primary file:text-white file:font-medium file:cursor-pointer hover:file:bg-primary/90 mb-4"
            />

            {csvError && (
              <p className="text-sm text-red-600 mb-3">{csvError}</p>
            )}

            {csvGuests.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-medium text-foreground mb-2">
                  Preview: {csvGuests.length} guest(s) found
                </p>
                <div className="max-h-40 overflow-y-auto border border-border rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-muted sticky top-0">
                      <tr>
                        <th className="text-left px-2 py-1">Name</th>
                        <th className="text-left px-2 py-1">Phone</th>
                        <th className="text-left px-2 py-1">Pax</th>
                      </tr>
                    </thead>
                    <tbody>
                      {csvGuests.slice(0, 10).map((g, i) => (
                        <tr key={i} className="border-t border-border">
                          <td className="px-2 py-1">{g.guest_name}</td>
                          <td className="px-2 py-1">{g.phone || '-'}</td>
                          <td className="px-2 py-1">{g.number_of_guests || 1}</td>
                        </tr>
                      ))}
                      {csvGuests.length > 10 && (
                        <tr className="border-t border-border">
                          <td colSpan={3} className="px-2 py-1 text-muted-foreground italic">
                            ...and {csvGuests.length - 10} more
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowCsvModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={handleCsvImport}
                className="flex-1"
                disabled={csvGuests.length === 0 || csvImporting}
              >
                {csvImporting ? 'Importing...' : `Import ${csvGuests.length} Guest(s)`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuestManagementTab;
